import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import { fetchBookingPipeline } from "../../../API_Service/Dashboard_Slice";
import { useDashboardSignalR } from "../../..//Common/useDashboardSignalR";

const STAGES = [
  { key: "requested", label: "Requested", color: "#4A90D9", icon: "➜" },
  {
    key: "pendingApproval",
    label: "Pending Approval",
    color: "#F5A623",
    icon: "⏱",
  },
  { key: "approved", label: "Approved", color: "#14B8A6", icon: "✔" },
  { key: "running", label: "Running", color: "#8E44AD", icon: "▶" },
  { key: "completed", label: "Completed", color: "#2ECC71", icon: "🏁" },
];

const BookingPipelineChart = ({ companyId }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    dispatch(fetchBookingPipeline({ companyId }))
      .unwrap()
      .then(setData)
      .catch((err) => setError(err?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [dispatch, companyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useDashboardSignalR(loadData);

  const total = data?.requested || 1;
  const conversionRate = data?.conversionRate
    ? parseFloat(data.conversionRate).toFixed(1)
    : "0.0";

  const chartData = useMemo(
    () => ({
      series: STAGES.map((s) => data?.[s.key] ?? 0),
      labels: STAGES.map((s) => s.label),
      colors: STAGES.map((s) => s.color),
    }),
    [data],
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
          export: {
            csv: {
              filename: "Booking_Pipeline",
              columnDelimiter: ",",
              headerCategory: "Stage",
              headerValue: "Bookings",
            },
            svg: { filename: "Booking_Pipeline" },
            png: { filename: "Booking_Pipeline" },
          },
        },
        fontFamily: "inherit",
      },
      colors: chartData.colors,
      labels: chartData.labels,
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "12px",
                color: "#6B7280",
              },
              value: {
                show: true,
                fontSize: "16px",
                fontWeight: 700,
                color: "#1F2937",
              },
              total: {
                show: true,
                label: "Total Bookings",
                fontSize: "12px",
                color: "#6B7280",
                formatter: () => total.toLocaleString(),
              },
            },
          },
        },
      },
      legend: {
        position: "bottom",
        labels: {
          colors: "#374151",
          useSeriesColors: false,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (_, { seriesIndex }) => {
          const actualValue = chartData.series[seriesIndex];
          const pct = ((actualValue / total) * 100).toFixed(1);
          return `${pct}%`;
        },
        style: {
          fontSize: "12px",
          fontWeight: 600,
          colors: ["#ffffff"],
        },
      },
      tooltip: {
        enabled: true,
        theme: "light",
        style: { fontSize: "12px" },
        y: {
          formatter: (val) => {
            const pct = ((val / total) * 100).toFixed(1);
            return `${val.toLocaleString()} bookings (${pct}%)`;
          },
        },
      },
    }),
    [chartData, total],
  );

  if (loading)
    return (
      <div className="pipeline-card">
        <div className="pipeline-loading">Loading...</div>
      </div>
    );

  if (error)
    return (
      <div className="pipeline-card">
        <div className="pipeline-error">{error}</div>
      </div>
    );

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 hover:shadow-lg transition-shadow duration-300">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-slate-900">Booking Pipeline</h3>
      </div>

      <div>
        {/* Conversion Summary - Top */}
        <div className="mb-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#08757C] to-[#0a9a99] p-6 shadow-lg border border-[#08757C]/30">
            <div className="absolute -right-20 -top-20 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -left-20 -bottom-20 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <p className="text-white/80 text-sm font-medium mb-2">Overall Conversion Rate</p>
              <div className="flex items-baseline gap-3">
                <p className="text-5xl font-bold text-white">{conversionRate}%</p>
                <p className="text-white/70 text-sm">
                  {(data?.completed ?? 0).toLocaleString()} of{" "}
                  {(data?.requested ?? 0).toLocaleString()} completed
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Donut Chart with Breakdown List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart - Left (1 column) */}
          <div className="lg:col-span-1 flex justify-center">
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-4 border border-slate-200/50 w-full overflow-visible">
              <div className="overflow-visible">
                <Chart
                  options={chartOptions}
                  series={chartData.series}
                  type="donut"
                  height={340}
                  width={340}
                />
              </div>
            </div>
          </div>

          {/* Breakdown List - Right (2 columns) */}
          <div className="lg:col-span-2 flex items-center">
            <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200/50 p-6 shadow-sm w-full">
              <p className="text-sm font-semibold text-slate-900 mb-5 px-2">Stage Breakdown</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {STAGES.map((stage) => {
                  const val = data?.[stage.key] ?? 0;
                  const pct = total ? ((val / total) * 100).toFixed(1) : "0.0";
                  return (
                    <div
                      key={stage.key}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-100/50 transition-colors duration-200 bg-white border border-slate-100/50"
                    >
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm"
                        style={{ backgroundColor: stage.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700">
                          {stage.label}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-slate-900">
                          {pct}%
                        </p>
                        <p className="text-xs text-slate-500">
                          {val.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPipelineChart;